import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  useGetSubscriptionTrialConfigQuery,
  useUpdateSubscriptionTrialConfigMutation,
} from '@/services/SubscriptionTrialConfigService';
import type { ApiResponse } from '@/types/api.types';
import type { SubscriptionTrialConfigUpdatePayload } from '@/types/subscription-trial-config.types';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import type { ISubscription } from '@/types/subscription-management.types';
import { SubscriptionType } from '@/constant';
import NumberInput from '@/components/inputControls/NumberInput';

const MIN_TRIAL_DAYS = 1;
const MAX_TRIAL_DAYS = 365;

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null) {
    const typedError = error as {
      data?: { message?: string };
      message?: string;
    };

    if (typedError.data?.message) return typedError.data.message;
    if (typedError.message) return typedError.message;
  }

  return fallback;
};

const stripHtmlTags = (html: string): string => {
  if (!html) return '';
  // Create a temporary div element to parse HTML
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  // Get text content and trim whitespace
  return tmp.textContent || tmp.innerText || '';
};

interface SubscriptionTrialFormProps {
  mode: 'view' | 'edit' | 'create';
  onDone: () => void;
  readOnlyReason?: string;
}

const SubscriptionTrialForm = ({
  mode,
  onDone,
  readOnlyReason,
}: SubscriptionTrialFormProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const { data, refetch } = useGetSubscriptionTrialConfigQuery();
  const [updateConfig, { isLoading: isUpdating }] =
    useUpdateSubscriptionTrialConfigMutation();

  const [subscriptions, setSubscriptions] = useState<ISubscription[]>([]);
  const [isSubscriptionsLoading, setIsSubscriptionsLoading] = useState(false);

  const [form, setForm] = useState<SubscriptionTrialConfigUpdatePayload>({
    subscription_id: '',
    trial_days: 14,
  });

  const [trialDaysText, setTrialDaysText] = useState<string>('14');

  useEffect(() => {
    if (data) {
      const api = data as ApiResponse<{
        subscription_id?: string;
        trial_subscription_id?: string;
        trial_days?: number;
      }>;
      if (api?.data) {
        const nextTrialDays =
          typeof api.data.trial_days === 'number' ? api.data.trial_days : 14;

        setForm({
          subscription_id: String(
            api.data.subscription_id ?? api.data.trial_subscription_id ?? ''
          ),
          trial_days: nextTrialDays,
        });

        setTrialDaysText(String(nextTrialDays));
      }
    }
  }, [data]);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setIsSubscriptionsLoading(true);
        const res = await SubscriptionsService.getSubscriptions(
          1,
          200,
          undefined,
          undefined,
          SubscriptionType.BOATER
        );
        if (!mounted) return;
        setSubscriptions(res.subscriptions ?? []);
      } catch (error: unknown) {
        enqueueSnackbar(
          getErrorMessage(error, 'Failed to load subscriptions'),
          { variant: 'error' }
        );
      } finally {
        if (mounted) setIsSubscriptionsLoading(false);
      }
    };

    load();

    return () => {
      mounted = false;
    };
  }, [enqueueSnackbar]);

  const isReadOnly = Boolean(readOnlyReason) || mode === 'view';

  const handleSave = async () => {
    if (isReadOnly) return;

    const normalizedTrialDaysText = trialDaysText.trim();
    const parsedTrialDays =
      normalizedTrialDaysText === ''
        ? Number.NaN
        : Number(normalizedTrialDaysText);

    if (!form.subscription_id) {
      enqueueSnackbar('Please select a subscription', { variant: 'error' });
      return;
    }

    if (
      !Number.isInteger(parsedTrialDays) ||
      parsedTrialDays < MIN_TRIAL_DAYS ||
      parsedTrialDays > MAX_TRIAL_DAYS
    ) {
      enqueueSnackbar(
        `Trial days must be between ${MIN_TRIAL_DAYS} and ${MAX_TRIAL_DAYS}`,
        { variant: 'error' }
      );
      return;
    }

    try {
      await updateConfig({ ...form, trial_days: parsedTrialDays }).unwrap();
      enqueueSnackbar('Subscription trial configuration updated successfully', {
        variant: 'success',
      });
      refetch();
      onDone();
    } catch (error: unknown) {
      enqueueSnackbar(
        getErrorMessage(error, 'Failed to update configuration'),
        {
          variant: 'error',
        }
      );
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {readOnlyReason && (
          <div className="md:col-span-2 text-sm text-gray-600">
            {readOnlyReason}
          </div>
        )}

        <div>
          <label className="form-label" htmlFor="trial-subscription">
            Subscription Plan
          </label>
          <select
            id="trial-subscription"
            className="select w-full"
            value={form.subscription_id}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                subscription_id: e.target.value,
              }))
            }
            disabled={isReadOnly || isSubscriptionsLoading}
          >
            <option value="">Select subscription plan</option>
            {subscriptions.map((s) => (
              <option key={s.id} value={s.id}>
                {stripHtmlTags(s.name)}
              </option>
            ))}
          </select>
          {isSubscriptionsLoading && (
            <div className="text-xs mt-1 text-gray-600">
              Loading subscriptions...
            </div>
          )}
        </div>

        <div>
          <label className="form-label" htmlFor="trial-days">
            Trial Period (Days)
          </label>
          <NumberInput
            id="trial-days"
            data-testid="trial-days-input"
            className="input w-full"
            min={MIN_TRIAL_DAYS}
            max={MAX_TRIAL_DAYS}
            value={trialDaysText}
            onChange={(e) => {
              const raw = e.target.value;

              if (raw === '') {
                setTrialDaysText('');
                return;
              }

              const normalized = raw.replace(/^0+(?=\d)/, '');
              if (!/^\d+$/.test(normalized)) return;

              setTrialDaysText(normalized);

              const nextValue = Number(normalized);
              if (Number.isNaN(nextValue)) return;

              setForm((prev) => ({
                ...prev,
                trial_days: nextValue,
              }));
            }}
            disabled={isReadOnly}
            autoComplete="off"
          />
          <div className="text-xs mt-1 text-gray-600">
            Number of days users can trial this plan ({MIN_TRIAL_DAYS}-
            {MAX_TRIAL_DAYS} days)
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <button type="button" className="btn btn-light" onClick={onDone}>
          {isReadOnly ? 'Back' : 'Cancel'}
        </button>

        {!isReadOnly && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isUpdating}
          >
            {isUpdating ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>
    </div>
  );
};

export { SubscriptionTrialForm };
